/* SEO head injector.

   Applies per-route metadata SYNCHRONOUSLY from the static registry
   (`src/lib/seoRegistry.js`) so that `react-snap` captures fully-formed
   <title>, meta description, canonical, OG, Twitter and JSON-LD tags in
   the HTML of each route.

   After the sync application, the component ALSO issues a background
   fetch to `/api/public/seo?path=…` to honor any admin-edited overrides.
   The DOM values are patched in place — the pre-rendered HTML remains
   correct on its own.

   Rule of thumb: never rely on the async patch to establish essential
   SEO metadata. The static registry is authoritative.
*/
import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import {
  canonicalFor,
  getSeoForPath,
  SOCIAL_IMAGE,
  CANONICAL_ORIGIN,
} from "../lib/seoRegistry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();

// react-snap sets navigator.userAgent to include "ReactSnap" — we key
// off it to skip async network patches so the snapshot is deterministic.
const IS_SNAP = typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "");

// ID used to identify the JSON-LD <script> we manage so we can replace it
// on route change instead of appending duplicates.
const JSONLD_ID = "fp-jsonld";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(nodes) {
  // Remove all previously-managed blocks (one per route change).
  document.head.querySelectorAll(`script[data-fp-jsonld="1"]`).forEach((n) => n.remove());
  if (!nodes || !nodes.length) return;
  nodes.forEach((obj, idx) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-fp-jsonld", "1");
    if (idx === 0) s.id = JSONLD_ID;
    s.text = JSON.stringify(obj);
    document.head.appendChild(s);
  });
}

function applyFromRegistry(pathname) {
  const seo = getSeoForPath(pathname);
  if (!seo) return null;
  const canonical = canonicalFor(pathname);
  const ogUrl = canonical;
  const ogImage = SOCIAL_IMAGE;

  document.title = seo.title;
  setMeta("description", seo.description);
  if (seo.keywords) setMeta("keywords", seo.keywords);
  setMeta("robots", "index,follow");
  setLink("canonical", canonical);

  setMeta("og:site_name", "FastPrototype.ai", "property");
  setMeta("og:type", seo.ogType || "website", "property");
  setMeta("og:title", seo.ogTitle || seo.title, "property");
  setMeta("og:description", seo.ogDescription || seo.description, "property");
  setMeta("og:url", ogUrl, "property");
  setMeta("og:image", ogImage, "property");
  setMeta("og:image:secure_url", ogImage, "property");
  setMeta("og:image:type", "image/png", "property");
  setMeta("og:image:width", "1200", "property");
  setMeta("og:image:height", "630", "property");
  setMeta("og:image:alt", seo.ogTitle || seo.title, "property");

  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", seo.ogTitle || seo.title);
  setMeta("twitter:description", seo.ogDescription || seo.description);
  setMeta("twitter:image", ogImage);
  setMeta("twitter:image:alt", seo.ogTitle || seo.title);
  setMeta("twitter:url", ogUrl);
  setMeta("twitter:domain", CANONICAL_ORIGIN.replace(/^https?:\/\//, ""));

  setJsonLd(seo.jsonLd || []);
  return seo;
}

function applyOverride(seoDoc, pathname) {
  // Soft override from the backend (admin edits). Only touch DOM if the
  // value differs from what the static registry produced. Never overwrites
  // canonical or JSON-LD — those must come from the static registry.
  if (!seoDoc) return;
  if (seoDoc.title && seoDoc.title !== document.title) document.title = seoDoc.title;
  if (seoDoc.description) setMeta("description", seoDoc.description);
  if (seoDoc.keywords) setMeta("keywords", seoDoc.keywords);
  if (seoDoc.noindex) setMeta("robots", "noindex,nofollow");
  if (seoDoc.og_title) setMeta("og:title", seoDoc.og_title, "property");
  if (seoDoc.og_description) setMeta("og:description", seoDoc.og_description, "property");
  // Never trust `og_image` for the primary social — it's often a small
  // logo. Only override when the admin has explicitly pointed to a
  // 1200x630-compliant URL AND the current route is NOT one we ship a
  // dedicated social image for. To keep things predictable, we simply
  // don't override og:image here.
}

/**
 * Mounted once at the top of <App />. On every route change, applies the
 * static SEO registry synchronously and then (optionally) patches with
 * admin overrides from the backend.
 */
export default function SEOHead() {
  const location = useLocation();
  const path = location.pathname || "/";

  // useLayoutEffect fires before paint, ensuring the snapshot always
  // reflects the correct metadata.
  useLayoutEffect(() => {
    applyFromRegistry(path);
  }, [path]);

  useEffect(() => {
    if (IS_SNAP) return; // deterministic snapshots — skip runtime override
    if (cache.has(path)) {
      applyOverride(cache.get(path), path);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/public/seo`, { params: { path } })
      .then((res) => {
        if (cancelled) return;
        cache.set(path, res.data);
        applyOverride(res.data, path);
      })
      .catch(() => { /* silent — static registry already applied */ });
    return () => { cancelled = true; };
  }, [path]);

  return null;
}

/** Allow the admin SEO editor to evict cached entries after saves. */
export function clearSeoCache(path) {
  if (path) cache.delete(path);
  else cache.clear();
}
